import PROCESS_TYPES from "@common/constants/processTypes";
import { USER_ACTION_IDS } from "@common/constants/useractionConstants";
import { MAX_CONSECUTIVE_UNCLEANED_BREWS } from "@common/constants/sessionConstants";

export const isDeviceActiveDisconnected = (device) =>
  (device.process_type == PROCESS_TYPES.PROC_BREWING ||
    device.process_type == PROCESS_TYPES.PROC_CLEAN_MINIBREW ||
    device.process_type == PROCESS_TYPES.PROC_FERMENTATION ||
    device.process_type == PROCESS_TYPES.PROC_SERVING) &&
  !device.online;

export const isDeviceDisconnected = (device) =>
  !device.online && !isDeviceActiveDisconnected(device);

export const isDeviceStarting = (device) => device.is_starting;

export const isDeviceUpdating = (device) =>
  !isDeviceDisconnected(device) &&
  !isDeviceActiveDisconnected(device) &&
  device.updating;

export const deviceNeedsAcidCleaning = (device) =>
  (device.user_action === USER_ACTION_IDS.ACTION_NEEDS_ACID_CLEANING ||
    device.needs_acid_cleaning) &&
  !isDeviceDisconnected(device) &&
  !device.updating &&
  device.process_type === PROCESS_TYPES.PROC_IDLE; // PROCESS_TYPES.PROC_IDLE is 0

export const deviceNeedsCleaning = (device) =>
  (device.user_action === USER_ACTION_IDS.ACTION_NEEDS_CLEANING ||
    Boolean(
      device.consecutive_uncleaned_brews &&
        device.consecutive_uncleaned_brews >= MAX_CONSECUTIVE_UNCLEANED_BREWS,
    )) &&
  !isDeviceDisconnected(device) &&
  !deviceNeedsAcidCleaning(device) &&
  !device.updating &&
  device.process_type === PROCESS_TYPES.PROC_IDLE;

// Gated on BOTH disconnect flags. `isDeviceDisconnected` alone does not cover
// an offline device mid-CIP: PROC_CLEAN_MINIBREW puts it in
// `isDeviceActiveDisconnected` instead, so the card used to render and either
// ack a teardown into the void or route to a carousel whose Continue is
// equally dead. The tile falls through to StatusBrewingDisconnected instead.
export const deviceFinishCleaning = (device) =>
  (device.user_action === USER_ACTION_IDS.ACTION_FINISH_CLEANING ||
    device.user_action === USER_ACTION_IDS.ACTION_FINISH_ACID_CLEANING) &&
  (device.process_type === PROCESS_TYPES.PROC_CLEAN_MINIBREW ||
    device.process_type === PROCESS_TYPES.PROC_ACID_CLEAN_MINIBREW) &&
  !isDeviceDisconnected(device) &&
  !isDeviceActiveDisconnected(device) &&
  !isDeviceUpdating(device);

export const deviceRequiresUserAction = (device) =>
  device.user_action !== 0 &&
  device.session_id != null &&
  !isDeviceDisconnected(device) &&
  !isDeviceUpdating(device) &&
  !deviceNeedsCleaning(device) &&
  !deviceNeedsAcidCleaning(device) &&
  !isDeviceActiveDisconnected(device) &&
  !deviceFinishCleaning(device);

export const isDeviceIdle = (device) =>
  device.process_type == PROCESS_TYPES.PROC_IDLE &&
  !isDeviceDisconnected(device) &&
  !deviceRequiresUserAction(device) &&
  !isDeviceUpdating(device) &&
  !deviceNeedsCleaning(device) &&
  !deviceNeedsAcidCleaning(device) &&
  !isDeviceStarting(device);

export const isDeviceCleaning = (device) =>
  device.process_type == PROCESS_TYPES.PROC_CLEAN_MINIBREW;

export const isDeviceActive = (device) =>
  (device.process_type == PROCESS_TYPES.PROC_BREWING ||
    device.process_type == PROCESS_TYPES.PROC_CLEAN_MINIBREW ||
    device.process_type == PROCESS_TYPES.PROC_FERMENTATION ||
    device.process_type == PROCESS_TYPES.PROC_SERVING) &&
  !deviceRequiresUserAction(device) &&
  !deviceFinishCleaning(device) &&
  !deviceNeedsCleaning(device) &&
  !deviceNeedsAcidCleaning(device) &&
  !isDeviceUpdating(device) &&
  !isDeviceDisconnected(device) &&
  !isDeviceActiveDisconnected(device);

export const isDeviceKeg = (device) => device.device_type === 1;

export const isDeviceIdleProcess = (device) =>
  device.process_type == PROCESS_TYPES.PROC_IDLE;

// A keg stranded by a failed fermentation swap/start (spec: fermentation-swap
// browser recovery, §4.1). Verified live 2026-08-30: a mid-fermentation STOP
// takes the keg STRAIGHT to user_action 38 (ACTION_KEG_NEEDS_CLEANING) while
// the backend's keep-alive leaves its session ACTIVE — so idle + gate-38 +
// ACTIVE session is the stranding signature. A deliberate end-of-batch reaches
// the same device state, which is why the tile this predicate drives OFFERS
// resume alongside cleaning instead of suppressing the cleaning path.
// user_action 38 exactly — 12 (ACTION_NEEDS_CLEANING) is the base unit's gate.
export const kegFermentationInterrupted = (device) =>
  isDeviceKeg(device) &&
  device.process_type === PROCESS_TYPES.PROC_IDLE &&
  device.user_action === USER_ACTION_IDS.ACTION_KEG_NEEDS_CLEANING &&
  device.session_id != null &&
  device.session_status === 1 && // SES_STATUS_ACTIVE
  !isDeviceDisconnected(device) &&
  !isDeviceUpdating(device);

// ── Command capability ──────────────────────────────────────────────────────
//
// Everything above answers "which tile should this device render?" — one
// interdependent display-state machine, where the predicates chain into each
// other and exactly one is meant to win. What follows answers a different
// question: "can this device receive a command right now?" It is a standalone
// capability check and deliberately does not participate in that chain.
//
// NOTE THE DIFFERING MISSING-FIELD STANCE, it is intentional. The display
// predicates above use `!device.online`, so a payload with no `online` key
// reads as disconnected — fail CLOSED, because showing a disconnected tile is
// safer than pretending a device is live. `isDeviceOffline` below uses
// `device.online === false`, so the same payload reads as online — fail OPEN,
// because grinding every control to disabled on an unanticipated payload shape
// is worse than letting the user try and having the backend refuse. Do not
// "harmonise" these two forms without understanding which way each one should
// fail.

// Whether a device is unreachable for device commands.
//
// The portal never publishes to a device itself — every command is a REST call
// to Django, which then publishes to the broker. When the device is not
// connected the frame is dropped: no `commandSeq` ack ever arrives, and the UI
// is left on a spinner or an 8-second timeout. So any entry point that ends in
// a device command must be gated on this.
//
// `online` is served by GET /v1/breweryoverview/ and derived backend-side as
// `connection_status == MACH_CONN_STATUS_ONLINE`, so NOT_RESPONDING (2) is
// already folded in — correct, since a not-responding device will not execute
// the command either.
//
// Do NOT reach for `isDeviceDisconnected` instead. That flag is
// `!online && !isDeviceActiveDisconnected` — i.e. offline AND IDLE. It is
// false by construction for an offline device that is mid-process, which is
// exactly the case "Edit fermentation" has to block. The older "Clean" gate
// gets away with `isDeviceDisconnected` only because Clean additionally
// requires an idle device.
//
// `=== false`, not `!device.online`: see the missing-field note above. Same
// default-open stance as `brewEligible`, and the same form `sessionStuck.js`
// already uses for this field.
//
// `deviceFinishCleaning` above deliberately does NOT use this predicate. It is
// built from the `isDeviceDisconnected` / `isDeviceActiveDisconnected` pair it
// already computes for other purposes. That is a real seam gap, not an
// oversight to silently fix: if this predicate's definition of "offline" ever
// grows (e.g. to factor in log staleness), the Finish-cleaning gate will not
// pick it up automatically. A future change here must also check
// `deviceFinishCleaning`.
export const isDeviceOffline = (device) => device.online === false;

export const OFFLINE_COMMAND_REASON =
  "Device is offline — it can't receive commands right now.";
